/**
 * Sanitization utilities to prevent injection attacks
 */

// Remove HTML tags and special characters that could be used for injection
export const sanitizeString = (str) => {
  if (typeof str !== 'string') return '';
  
  return str
    .replace(/[<>{}[\]\\]/g, '') // Remove potential injection characters
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers
    .trim()
    .slice(0, 10000); // Limit length
};

// Sanitize URLs - only allow http/https or relative paths starting with /
export const sanitizeURL = (url) => {
  if (typeof url !== 'string') return '';
  
  const trimmedUrl = url.trim();
  
  // Allow empty URLs
  if (trimmedUrl === '') return '';
  
  // Allow relative paths (for internal navigation like /group, /mobile)
  if (trimmedUrl.startsWith('/')) {
    // Only allow alphanumeric, /, -, _, and ? for query params
    if (/^\/[a-zA-Z0-9\-_/?&=]*$/.test(trimmedUrl)) {
      return trimmedUrl.slice(0, 200); // Limit relative path length
    }
    return '';
  }
  
  // Check if it's a valid http/https URL
  try {
    const urlObj = new URL(trimmedUrl);
    if (urlObj.protocol === 'http:' || urlObj.protocol === 'https:') {
      return trimmedUrl.slice(0, 2048); // Limit URL length
    }
  } catch (e) {
    // Invalid URL
  }
  
  return '';
};

// Sanitize YouTube URL and extract video ID
export const sanitizeYouTubeURL = (url) => {
  if (typeof url !== 'string') return '';
  
  const trimmedUrl = url.trim();
  if (trimmedUrl === '') return '';
  
  // Only allow YouTube URLs
  const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
  
  if (!youtubeRegex.test(trimmedUrl)) {
    return '';
  }
  
  return sanitizeURL(trimmedUrl);
};

// Sanitize array of strings
export const sanitizeArray = (arr) => {
  if (!Array.isArray(arr)) return [];
  
  return arr
    .map(item => sanitizeString(item))
    .filter(item => item.length > 0)
    .slice(0, 100); // Limit array size
};

// Validate and sanitize number strings
export const sanitizeNumber = (str) => {
  if (typeof str !== 'string') return '';
  
  // Allow numbers, +, %, K, M (for stats like 500+, 98%, 15K+)
  return str
    .replace(/[^0-9+%KMkm.,\s]/g, '')
    .trim()
    .slice(0, 20);
};

// Add metadata to all write operations
export const addMetadata = (data, operation = 'update') => {
  const metadata = {
    updatedAt: new Date().toISOString(),
    updatedBy: 'admin'
  };
  
  if (operation === 'create') {
    metadata.createdAt = new Date().toISOString();
    metadata.createdBy = 'admin';
  }
  
  return {
    ...data,
    ...metadata
  };
};

// Validate data structure before saving
export const validateServiceData = (data) => {
  if (!data.title || typeof data.title !== 'string' || data.title.length > 100) {
    throw new Error('Invalid title');
  }
  
  if (!data.description || typeof data.description !== 'string' || data.description.length > 1000) {
    throw new Error('Invalid description');
  }
  
  return true;
};

export const validateStatData = (data) => {
  if (!data.number || typeof data.number !== 'string' || data.number.length > 20) {
    throw new Error('Invalid number');
  }
  
  if (!data.label || typeof data.label !== 'string' || data.label.length > 100) {
    throw new Error('Invalid label');
  }
  
  return true;
};

export const validateTrainerData = (data) => {
  if (!data.name || typeof data.name !== 'string' || data.name.length > 100) {
    throw new Error('Invalid name');
  }
  
  if (!data.specialty || typeof data.specialty !== 'string' || data.specialty.length > 200) {
    throw new Error('Invalid specialty');
  }
  
  if (data.imageUrl && !sanitizeURL(data.imageUrl)) {
    throw new Error('Invalid image URL');
  }
  
  return true;
};
